import {
  ENUM_FIELDS,
  FIELD_NOTES,
  NAMESPACE_DOCS,
  RuleDictionary,
  RuleFunctionSignature,
  classifyDictionaryKey,
  describeFunction,
  parseSignature
} from './ruleDictionary';

/**
 * The rule language guide.
 *
 * Two halves. The prose below is written by hand and says what the engine does with a rule -
 * nothing generates that. The reference underneath it is built from the live dictionary, so the
 * functions and constants a reader sees are the ones the service they are talking to actually has,
 * and a helper added there is documented the day it ships instead of the day someone remembers to
 * come back here. `ruleDictionary.ts` holds the per-function prose the two share with the editor's
 * completion list.
 */
const RULE_GUIDE = `
Rules are written in **JEXL**, evaluated by the service against one class at a time. A rule answers
a single question: *does this class deserve the flag?*

### How a rule is evaluated

* The rule runs once per class, with \`klass\` bound to the class being examined.
* The **last expression is the answer**, and it has to be \`true\` or \`false\`. Anything else, and
  the evaluation of that class fails.
* \`true\` raises the flag. \`false\` retires one that was raised earlier — a flag disappears by
  itself once its rule stops matching, so rules are written as *the problem*, never as the fix.
* Statements end with \`;\`, \`//\` starts a comment, and \`var\` declares a working variable.
* A rule that cannot be evaluated — a misspelled field, a misspelled enum — is logged and skipped,
  and the class keeps whatever flags it already had. The check under the editor is **syntax only**:
  it never resolves a name. **Preview matches** is what proves a rule works.

### Operators

\`==\` \`!=\` \`<\` \`<=\` \`>\` \`>=\` for comparison, \`&&\` \`||\` \`!\` for logic (\`and\`, \`or\`
and \`not\` also work), \`+\` \`-\` \`*\` \`/\` for arithmetic.

* \`empty(value)\` — true for null, for a blank string and for an empty collection.
* \`size(value)\` — items in a collection, characters in a string.
* \`'' + value\` — turns a number into text, which matters when comparing against a text column.

### Control flow

\`\`\`javascript
if (klass.type == KlassType.PRIVATE) {
    // ...
} else if (klass.type == KlassType.OPEN_ENROLMENT) {
    // ...
}

// A colon, not "of" or "in"
for (var instructorId : db:effectiveInstructorIds(klass.id)) {
    // ...
}
\`\`\`

### Reaching the data

Dot into \`klass\` for its fields, and keep going: \`klass.deliveryAddress.country.name\`. A path
through a relationship that is not set evaluates to null rather than failing, so
\`klass.facility.name\` is safe on a class with no facility. A **misspelled** field is not: it fails
the rule.

* Statuses, types, modalities and decisions are enums. Compare them with the constants —
  \`klass.status == KlassStatus.CONFIRMED\` — never with the quoted name.
* \`klass.startDateTime\` and \`klass.endDateTime\` carry a zone. Use the \`time:\` helpers rather
  than comparing them by hand.
* Everything the editor offers after a dot comes from the service, so the list is always the fields
  the deployed version really has.

### A class has several instructors

There is no single instructor field. Three collections hold them, in order of precedence:

* \`klass.instructors\` — requested
* \`klass.allocatedInstructors\` — planned
* \`klass.finalInstructors\` — confirmed, and the ones that win

\`klass.instructorId\`, \`klass.allocatedInstructorId\` and \`klass.finalInstructorId\` no longer
resolve, and a rule still using one fails every time it runs. Test presence with \`empty(...)\`, and
let \`db:effectiveInstructorIds(klass.id)\` walk the precedence chain when the rule is about whoever
is actually teaching.

### Functions take a colon

\`time:\` and \`db:\` are namespaces, not objects: it is \`time:startsWithinWeeks(...)\`, never
\`time.startsWithinWeeks(...)\`. The dotted form passes the syntax check and then fails at
evaluation, which is the most expensive way to find out. Type \`db:\` in the editor and the list
appears; accept a suggestion after typing a dot by mistake and the dot is corrected for you.

### A worked example

Two classes at once, for any of the instructors actually teaching this one:

\`\`\`javascript
var hasConflict = false;

for (var instructorId : db:effectiveInstructorIds(klass.id)) {
    var others = db:klassesByEffectiveInstructor(instructorId, klass.id);

    for (var other : others) {
        if (time:isOverlapping(klass.startDateTime, klass.endDateTime,
                               other.startDateTime, other.endDateTime)) {
            hasConflict = true;
        }
    }
}

hasConflict;
\`\`\`

### In the editor

* \`.\` after a field, \`:\` after a namespace, or **Ctrl+Space** anywhere, opens the suggestions.
  Each one carries its full path or signature and a description.
* Typing inside a function's brackets shows its parameters.
* **Preview matches** runs the rule over the database and lists what it catches. Nothing is saved,
  and no flags are raised, until you press Save.
`;

/** The reference, built from whatever the service currently answers with. */
const functionReference = (dictionary: RuleDictionary, namespace: string): string => {
  const overloads = new Map<string, RuleFunctionSignature[]>();

  for (const entry of dictionary[namespace] ?? []) {
    const signature = parseSignature(entry);
    if (!signature) continue;
    overloads.set(signature.name, [...(overloads.get(signature.name) ?? []), signature]);
  }

  const sections = [...overloads.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, signatures]) => {
      // Spelled out rather than given as a range: `hasRecord` takes 3, 5, 7 or 9 - an entity and
      // then pairs - and "3 to 9" would invite the four that does not exist.
      const arities = [...new Set(signatures.map((signature) => signature.paramTypes.length))].sort(
        (a, b) => a - b
      );
      const longest = signatures.reduce((widest, signature) =>
        signature.paramTypes.length > widest.paramTypes.length ? signature : widest
      );
      const described = describeFunction(namespace, longest);

      return [
        `#### \`${namespace}:${described.label}\``,
        arities.length > 1
          ? `*Takes ${arities.slice(0, -1).join(', ')} or ${arities[arities.length - 1]} arguments.*`
          : '',
        described.documentation
      ]
        .filter(Boolean)
        .join('\n\n');
    });

  return [`### \`${namespace}:\` functions`, NAMESPACE_DOCS[namespace] ?? '', ...sections]
    .filter(Boolean)
    .join('\n\n');
};

// Lists rather than a table, throughout: the markdown here is rendered by react-markdown with no
// GFM plugin, so a table arrives on screen as its own pipes.
const enumReference = (dictionary: RuleDictionary, names: string[]): string => {
  const rows = names.sort().map((name) => {
    const field = ENUM_FIELDS[name] ? `, the values \`${ENUM_FIELDS[name]}\` holds` : '';
    const values = (dictionary[name] ?? []).map((value) => `\`${value}\``).join(' · ');
    return `* **${name}**${field} — ${values}`;
  });

  return ['### Enum constants', ...rows].join('\n');
};

const fieldReference = (dictionary: RuleDictionary, roots: string[]): string => {
  // Only the notes for paths this service really has: a field renamed away should take its note
  // with it rather than sit in the guide as advice about something that no longer exists.
  const notable = roots.flatMap((root) =>
    Object.entries(FIELD_NOTES)
      .filter(
        ([path]) =>
          path.startsWith(`${root}.`) && (dictionary[root] ?? []).includes(path.slice(root.length + 1))
      )
      .map(([path, note]) => `* \`${path}\` — ${note}`)
  );

  const counts = roots.map((root) => `\`${root}\` (${(dictionary[root] ?? []).length} paths)`).join(', ');

  return [
    '### Data',
    `The editor completes every field the service exposes: ${counts}. The ones worth a word:`,
    ...notable
  ].join('\n\n');
};

/**
 * The whole guide for a given dictionary. Pass an empty one — a dictionary that failed to load —
 * and the hand-written half is still returned, with the reference replaced by a note saying why it
 * is missing.
 */
export const buildRuleHelpMarkdown = (dictionary: RuleDictionary): string => {
  const keys = Object.keys(dictionary);

  if (!keys.length) {
    return [
      RULE_GUIDE,
      '---',
      '### Reference unavailable',
      'The list of functions, fields and constants comes from the service, and it could not be ' +
        'read. Autocomplete is unavailable for the same reason — reopen the page once the service ' +
        'is reachable.'
    ].join('\n\n');
  }

  const namespaces = keys.filter((key) => classifyDictionaryKey(dictionary[key]) === 'namespace').sort();
  const enums = keys.filter((key) => classifyDictionaryKey(dictionary[key]) === 'enum');
  const objects = keys.filter((key) => classifyDictionaryKey(dictionary[key]) === 'object').sort();

  return [
    RULE_GUIDE,
    '---',
    ...namespaces.map((namespace) => functionReference(dictionary, namespace)),
    enums.length ? enumReference(dictionary, enums) : '',
    objects.length ? fieldReference(dictionary, objects) : ''
  ]
    .filter(Boolean)
    .join('\n\n');
};
